/**
 * navigationTree.js — Phase N3.2 single source of truth for the
 * authenticated-shell navigation.
 *
 * ONE config, TWO consumers:
 *   - Desktop sidebar (persistent left rail)
 *   - Mobile hamburger (full-screen modal)
 *
 * Both surfaces render from `NAV_TREE` (customer-facing 8 groups) or
 * `ADMIN_NAV` (admin-only Admin section, hidden for non-admins).
 *
 * Every item's visibility is filtered client-side via `isItemVisible`
 * against the current `{ user, flags }` shape. Filtering happens BEFORE
 * render, and groups whose children all filter out are dropped entirely
 * (no orphan empty section headers).
 *
 * Guardrails encoded here:
 *   - No "coming soon" placeholders. Every item's `path` must resolve
 *     to a real route in `App.js`. The `navigation_tree.test.jsx`
 *     suite asserts this at build time.
 *   - No renamed destination labels — locked per Q3=A from N3.1. If
 *     a label needs to change, that is a separate coordinated phase.
 *   - Voice-guardrail regex sweep on every visible string in this
 *     file — asserted in the test suite.
 *
 * Related files:
 *   /app/memory/design/phase_n3_route_inventory.md   — the N3.1 audit
 *   /app/memory/design/phase_n3_2_navigation_note.md — the N3.2 note
 */

import {
  LayoutDashboard,
  UserCircle,
  Landmark,
  FileStack,
  Award,
  Users,
  Compass,
  Settings,
  Shield,
  Sparkles,
} from "lucide-react";

/* -------------------------------------------------------------------- */
/* Customer-facing tree (8 top-level groups)                             */
/* -------------------------------------------------------------------- */

export const NAV_TREE = [
  {
    key: "overview",
    label: "Overview",
    icon: LayoutDashboard,
    items: [
      { key: "dashboard", label: "Dashboard", path: "/dashboard", requireAuth: true },
    ],
  },
  {
    key: "financial_profile",
    label: "Financial Profile",
    icon: UserCircle,
    items: [
      { key: "profile",          label: "Profile",              path: "/profile",         requireAuth: true },
      { key: "readiness",        label: "Readiness Assessment", path: "/readiness",       requireAuth: true },
      { key: "bank_statements",  label: "Bank Statements",      path: "/bank-statements", requireAuth: true },
      { key: "documents",        label: "Documents",            path: "/documents",       requireAuth: true, requireFeatureFlag: "business_document_vault_v1" },
    ],
  },
  {
    key: "credit",
    label: "Credit",
    icon: FileStack,
    items: [
      { key: "credit_improvement", label: "Credit Improvement", path: "/credit-improvement", requireAuth: true },
      { key: "dispute_letters",    label: "Dispute Letters",    path: "/dispute-letters",    requireAuth: true },
      { key: "mailed_letters",     label: "Mailed Letters",     path: "/mailed-letters",     requireAuth: true },
      { key: "comparison",         label: "Comparison",         path: "/comparison",         requireAuth: true },
    ],
  },
  {
    key: "funding",
    label: "Funding Navigator",
    icon: Landmark,
    items: [
      { key: "opportunities",   label: "Opportunities",         path: "/opportunities",   requireAuth: true },
      { key: "partners",        label: "Funding Matches",       path: "/partners",        requireAuth: true },
      { key: "mortgage_intake", label: "Mortgage Readiness",    path: "/mortgage-intake", requireAuth: true },
    ],
  },
  {
    key: "grants",
    label: "Grant Navigator",
    icon: Award,
    items: [
      { key: "grants",         label: "Grant Overview",  path: "/grants",         requireAuth: true, requireFeatureFlag: "grant_navigator_v1" },
      { key: "grants_saved",   label: "Saved Grants",    path: "/grants/saved",   requireAuth: true, requireFeatureFlag: "grant_navigator_v1" },
      { key: "grants_matches", label: "Top Matches",     path: "/grants/matches", requireAuth: true, requireFeatureFlag: "grant_navigator_v1" },
      { key: "grants_pipeline",label: "Pipeline",        path: "/grants/pipeline",requireAuth: true, requireFeatureFlag: "grant_navigator_v1" },
      { key: "calendar",       label: "Calendar",        path: "/calendar",       requireAuth: true, requireFeatureFlag: "calendar_v1" },
      { key: "proposals",      label: "Proposals",       path: "/proposals",      requireAuth: true, requireFeatureFlag: "grant_writer_v1" },
    ],
  },
  {
    key: "marketplace",
    label: "Marketplace",
    icon: Users,
    items: [
      { key: "professional_marketplace", label: "Professional Marketplace", path: "/marketplace", requireAuth: true },
    ],
  },
  {
    key: "strategic_plan",
    label: "Strategic Plan",
    icon: Compass,
    items: [
      { key: "roadmap", label: "Roadmap", path: "/roadmap", requireAuth: true },
    ],
  },
  {
    key: "account",
    label: "Account",
    icon: Settings,
    items: [
      { key: "account_billing",     label: "Subscription and Billing", path: "/account/billing",            requireAuth: true },
      { key: "notifications",       label: "Notifications",            path: "/notifications",              requireAuth: true, requireFeatureFlag: "notifications_v1" },
      { key: "email_preferences",   label: "Email preferences",        path: "/settings/email-preferences", requireAuth: true },
    ],
  },
  {
    key: "owner_access",
    label: "Owner Access",
    icon: Sparkles,
    items: [
      { key: "owner_academy_ask",   label: "Ask Informed",             path: "/academy/ask",                requireAuth: true, requireOwner: true },
    ],
  },
];

/* -------------------------------------------------------------------- */
/* Admin-only section (role-gated, sits BELOW customer tree in sidebar)  */
/* -------------------------------------------------------------------- */

export const ADMIN_NAV = [
  {
    key: "admin",
    label: "Admin",
    icon: Shield,
    items: [
      { key: "admin_overview",       label: "Overview",           path: "/admin",                   requireAuth: true, requireAdmin: true },
      { key: "admin_users",          label: "Users",              path: "/admin/users",             requireAuth: true, requireAdmin: true },
      { key: "admin_team",           label: "Team",               path: "/admin/team",              requireAuth: true, requireAdmin: true },
      { key: "admin_partners",       label: "Partners",           path: "/admin/partners",          requireAuth: true, requireAdmin: true },
      { key: "admin_marketplace",    label: "Marketplace",        path: "/admin/marketplace",       requireAuth: true, requireAdmin: true },
      { key: "admin_grants",         label: "Grants",             path: "/admin/grants",            requireAuth: true, requireAdmin: true },
      { key: "admin_letters",        label: "Letters",            path: "/admin/letters",           requireAuth: true, requireAdmin: true },
      { key: "admin_credit_reports", label: "Credit Reports",     path: "/admin/credit-reports",    requireAuth: true, requireAdmin: true },
      { key: "admin_bank_statements",label: "Bank Statements",    path: "/admin/bank-statements",   requireAuth: true, requireAdmin: true },
      { key: "admin_mailed_letters", label: "Mailed Letters",     path: "/admin/mailed-letters",    requireAuth: true, requireAdmin: true },
      { key: "admin_plans",          label: "Plans",              path: "/admin/plans",             requireAuth: true, requireAdmin: true },
      { key: "admin_coupons",        label: "Coupons",            path: "/admin/coupons",           requireAuth: true, requireAdmin: true },
      { key: "admin_churn",          label: "Churn",              path: "/admin/churn",             requireAuth: true, requireAdmin: true },
      { key: "admin_revenue",        label: "Revenue",            path: "/admin/revenue",           requireAuth: true, requireAdmin: true },
      { key: "admin_billing",        label: "Billing",            path: "/admin/billing",           requireAuth: true, requireAdmin: true },
      { key: "admin_site_settings",  label: "Site Settings",      path: "/admin/site-settings",     requireAuth: true, requireAdmin: true },
      { key: "admin_system_config",  label: "System Config",      path: "/admin/system-config",     requireAuth: true, requireAdmin: true },
      { key: "admin_feature_flags",  label: "Feature Flags",      path: "/admin/feature-flags",     requireAuth: true, requireAdmin: true },
      { key: "admin_scoring_rubric", label: "Scoring Rubric",     path: "/admin/scoring-rubric",    requireAuth: true, requireAdmin: true },
      { key: "admin_mortgage_config",label: "Mortgage Config",    path: "/admin/mortgage-config",   requireAuth: true, requireAdmin: true },
      { key: "admin_credit_catalog", label: "Credit Catalog",     path: "/admin/credit-catalog",    requireAuth: true, requireAdmin: true },
      { key: "admin_forecast_config",label: "Forecast Config",    path: "/admin/forecast-config",   requireAuth: true, requireAdmin: true },
      { key: "admin_credit_tier_rates",label: "Credit Tier Rates",path: "/admin/credit-tier-rates", requireAuth: true, requireAdmin: true },
      { key: "admin_ai_logs",        label: "AI Logs",            path: "/admin/ai-logs",           requireAuth: true, requireAdmin: true },
      { key: "admin_email_logs",     label: "Email Logs",         path: "/admin/email-logs",        requireAuth: true, requireAdmin: true },
      { key: "admin_email_templates",label: "Email Templates",    path: "/admin/email-templates",   requireAuth: true, requireAdmin: true },
      { key: "admin_audit_logs",     label: "Audit Logs",         path: "/admin/audit-logs",        requireAuth: true, requireAdmin: true },
      { key: "admin_beta_codes",     label: "Early Access Codes", path: "/admin/beta-codes",        requireAuth: true, requireAdmin: true },
      { key: "admin_beta_waitlist",  label: "Beta Waitlist",      path: "/admin/beta-waitlist",     requireAuth: true, requireAdmin: true },
      { key: "admin_beta_feedback",  label: "Feedback",           path: "/admin/beta-feedback",     requireAuth: true, requireAdmin: true },
      { key: "admin_analytics",      label: "Analytics",          path: "/admin/analytics/funnel",  requireAuth: true, requireAdmin: true },
    ],
  },
];

/* -------------------------------------------------------------------- */
/* Visibility filter                                                     */
/* -------------------------------------------------------------------- */

/**
 * Return true when `item` is visible for the current user + flags.
 * Hide-if-unauthorized per Q2=A. Order matters: cheaper checks first.
 */
export function isItemVisible(item, { user, flags } = {}) {
  if (item?.requireAuth && !user) return false;
  if (item?.requireAdmin && user?.role !== "admin") return false;
  if (item?.requireOwner && user?.team_role !== "owner") return false;
  if (item?.requireTier === "premium" && user?.access_level !== "premium") return false;
  if (item?.requireFeatureFlag && !flags?.[item.requireFeatureFlag]) return false;
  return true;
}

/**
 * Filter a nav tree (array of groups) against the current context. A
 * group whose children all filter out is dropped entirely — no empty
 * headers in the final render. Preserves group ordering.
 */
export function filterTree(tree, ctx) {
  const out = [];
  for (const group of tree) {
    const items = (group.items || []).filter((it) => isItemVisible(it, ctx));
    if (items.length > 0) out.push({ ...group, items });
  }
  return out;
}

/**
 * Given a pathname, return the key of the group whose items include a
 * matching item. Used to auto-expand the current-page's group. Match
 * is exact-path first, then longest-prefix (to catch detail routes
 * like `/grants/:id` when the item is `/grants`).
 */
export function findGroupForPath(tree, pathname) {
  if (!pathname) return null;
  // Exact match first.
  for (const group of tree) {
    for (const item of group.items || []) {
      if (item.path === pathname) return group.key;
    }
  }
  // Longest-prefix match — catches `/dispute-letters/new`, `/grants/:id`, etc.
  let best = null;
  let bestLen = 0;
  for (const group of tree) {
    for (const item of group.items || []) {
      if (
        item.path &&
        pathname.startsWith(item.path + "/") &&
        item.path.length > bestLen
      ) {
        best = group.key;
        bestLen = item.path.length;
      }
    }
  }
  return best;
}

/**
 * Given a pathname, return the item.key of the exact-matching item
 * (or null). Used for `aria-current="page"`.
 */
export function findActiveItemKey(tree, pathname) {
  if (!pathname) return null;
  for (const group of tree) {
    for (const item of group.items || []) {
      if (item.path === pathname) return item.key;
    }
  }
  return null;
}
